import { apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';

export type DealStage = 'New' | 'Contacted' | 'Negotiation' | 'Won' | 'Lost';

export interface DealContact {
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface DealOwner {
  id: string;
  name?: string | null;
  email?: string | null;
}

export interface DealActivityItem {
  id: string;
  kind: string;
  actor: string;
  subject?: string | null;
  payload?: { body?: string } | null;
  createdAt: string;
  occurredAt?: string;
}

export interface DealListItem {
  id: string;
  workspaceId: string;
  title: string;
  amount: number | string;
  currency: 'RUB';
  stage: DealStage;
  contactId: string | null;
  ownerId: string | null;
  tags: string[];
  nextActivityDue: string | null;
  lastActivityAt: string | null;
  updatedAt?: string;
  contact?: DealContact | null;
  owner?: DealOwner | null;
}

export interface DealDetail extends DealListItem {
  source?: string | null;
  activities: DealActivityItem[];
  notes?: string | null;
}

export interface ListDealsFilters {
  stage?: DealStage;
}

export interface UpdateDealStageInput {
  toStage: DealStage;
}

export interface UpdateDealInput {
  title?: string;
  amount?: number;
  source?: string | null;
  nextActivityDue?: string | null;
}

export interface AddDealNoteInput {
  body: string;
}

export interface CreateDealInput {
  title: string;
  amount: number;
  stage?: DealStage;
  contactId: string;
}

export interface DealStageChangedEvent {
  type: 'deal.stage.changed';
  workspaceId: string;
  dealId: string;
  fromStage: DealStage;
  toStage: DealStage;
}

export function getDealsQueryKey(workspaceId: string, filters: ListDealsFilters = {}): readonly [string, string, string] {
  return ['deals', workspaceId, filters.stage ?? 'all'] as const;
}

export function getDealDetailQueryKey(workspaceId: string, dealId: string): readonly [string, string, string] {
  return ['deal', workspaceId, dealId] as const;
}

export async function listDeals(filters: ListDealsFilters = {}): Promise<DealListItem[]> {
  const search = new URLSearchParams();
  if (filters.stage !== undefined) {
    search.set('stage', filters.stage);
  }

  const path = search.size > 0 ? `/deals?${search.toString()}` : '/deals';
  return apiGet<DealListItem[]>(path);
}

export async function createDeal(input: CreateDealInput): Promise<DealListItem> {
  return apiPost<DealListItem, CreateDealInput>('/deals', input);
}

export async function updateDealStage(dealId: string, input: UpdateDealStageInput): Promise<DealListItem> {
  return apiPatch<DealListItem, UpdateDealStageInput>(`/deals/${dealId}/stage`, input);
}

export async function updateDeal(dealId: string, input: UpdateDealInput): Promise<DealDetail> {
  return apiPatch<DealDetail, UpdateDealInput>(`/deals/${dealId}`, input);
}

export async function addDealNote(dealId: string, input: AddDealNoteInput): Promise<DealActivityItem> {
  return apiPost<DealActivityItem, AddDealNoteInput>(`/deals/${dealId}/notes`, input);
}

export async function getDealById(dealId: string): Promise<DealDetail> {
  return apiGet<DealDetail>(`/deals/${dealId}`);
}
